import React, { createContext, useContext, useState, useCallback } from "react";
import { Place } from "../Place";
import { PlanSalida, construirPlan, DURACION_POR_TIPO } from "./planSalidaUtils";

const MAX_PLACES = 4;

interface PlanSalidaContextType {
  // Selección
  modoSeleccion:         boolean;
  placesSeleccionados:   Place[];
  isPlaceSeleccionado:   (id: string) => boolean;
  ordenPlace:            (id: string) => number | null;
  puedeAgregarPlace:     boolean;
  toggleModoSeleccion:   () => void;
  togglePlace:           (place: Place) => void;
  limpiarSeleccion:      () => void;

  // Configuración
  horaInicio:            string;   // "HH:mm"
  setHoraInicio:         (h: string) => void;
  duracionesCustom:      Record<string, number>;
  setDuracionCustom:     (placeId: string, min: number) => void;

  // Plan generado
  planGenerado:          PlanSalida | null;
  generarPlan:           () => void;
  limpiarPlan:           () => void;
}

const PlanSalidaContext = createContext<PlanSalidaContextType | null>(null);

const horaDefault = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 30);
  return d.toTimeString().slice(0, 5);
};

export const PlanSalidaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modoSeleccion,       setModoSeleccion]       = useState(false);
  const [placesSeleccionados, setPlacesSeleccionados] = useState<Place[]>([]);
  const [horaInicio,          setHoraInicio]          = useState(horaDefault);
  const [duracionesCustom,    setDuracionesCustom]    = useState<Record<string, number>>({});
  const [planGenerado,        setPlanGenerado]        = useState<PlanSalida | null>(null);

  const isPlaceSeleccionado = useCallback(
    (id: string) => placesSeleccionados.some(p => p.id === id),
    [placesSeleccionados],
  );

  const ordenPlace = useCallback(
    (id: string) => {
      const idx = placesSeleccionados.findIndex(p => p.id === id);
      return idx === -1 ? null : idx + 1;
    },
    [placesSeleccionados],
  );

  const togglePlace = useCallback((place: Place) => {
    setPlacesSeleccionados(prev => {
      if (prev.some(p => p.id === place.id)) {
        return prev.filter(p => p.id !== place.id);
      }
      if (prev.length >= MAX_PLACES) return prev;
      return [...prev, place];
    });
  }, []);

  const toggleModoSeleccion = useCallback(() => {
    setModoSeleccion(v => {
      if (v) {
        // al desactivar, limpiar selección si no hay plan
        setPlacesSeleccionados([]);
      }
      return !v;
    });
  }, []);

  const limpiarSeleccion = useCallback(() => {
    setPlacesSeleccionados([]);
    setModoSeleccion(false);
    setPlanGenerado(null);
  }, []);

  const setDuracionCustom = useCallback((placeId: string, min: number) => {
    setDuracionesCustom(prev => ({ ...prev, [placeId]: min }));
  }, []);

  const generarPlan = useCallback(() => {
    if (placesSeleccionados.length < 2) return;
    const [hh, mm] = horaInicio.split(':').map(Number);
    const inicio = new Date();
    inicio.setHours(hh, mm, 0, 0);
    setPlanGenerado(construirPlan(placesSeleccionados, inicio, duracionesCustom));
  }, [placesSeleccionados, horaInicio, duracionesCustom]);

  const limpiarPlan = useCallback(() => {
    setPlanGenerado(null);
    setPlacesSeleccionados([]);
    setModoSeleccion(false);
    setDuracionesCustom({});
    setHoraInicio(horaDefault());
  }, []);

  return (
    <PlanSalidaContext.Provider value={{
      modoSeleccion,
      placesSeleccionados,
      isPlaceSeleccionado,
      ordenPlace,
      puedeAgregarPlace: placesSeleccionados.length < MAX_PLACES,
      toggleModoSeleccion,
      togglePlace,
      limpiarSeleccion,
      horaInicio,
      setHoraInicio,
      duracionesCustom,
      setDuracionCustom,
      planGenerado,
      generarPlan,
      limpiarPlan,
    }}>
      {children}
    </PlanSalidaContext.Provider>
  );
};

export const usePlanSalida = () => {
  const ctx = useContext(PlanSalidaContext);
  if (!ctx) throw new Error("usePlanSalida debe usarse dentro de PlanSalidaProvider");
  return ctx;
};
